import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

const StopCustomizer = ({ trip, onUpdateTrip }) => {
  const [stops, setStops] = useState(trip.trip);
  const [newStopName, setNewStopName] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  
  // Handle removing a stop
  const handleRemoveStop = (index) => {
    // Don't allow removing start or end locations
    if (stops[index].type === 'start' || stops[index].type === 'end') {
      return;
    }
    
    const updatedStops = [...stops];
    updatedStops.splice(index, 1);
    
    setStops(updatedStops);
  };
  
  // Handle opening the add stop dialog
  const handleOpenAddDialog = () => {
    setNewStopName('');
    setOpenDialog(true);
  };
  
  // Handle closing the add stop dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  // Handle adding a new stop
  const handleAddStop = () => {
    if (!newStopName.trim()) {
      return;
    }
    
    // Add new stop before the end point
    const endIndex = stops.length - 1;
    const newStop = {
      name: newStopName,
      latitude: 0, // These will be set by the backend
      longitude: 0,
      type: 'intermediate'
    };
    
    const updatedStops = [...stops];
    updatedStops.splice(endIndex, 0, newStop);
    
    setStops(updatedStops);
    setOpenDialog(false);
  };
  
  // Handle saving the updated trip
  const handleSaveChanges = () => {
    // Create lists of stops to include and exclude
    const includeStops = stops.filter(stop => 
      stop.type === 'intermediate'
    ).map(stop => stop.name);
    
    // Create trip creation request data
    const requestData = {
      start_location: stops[0].name,
      end_location: stops[stops.length - 1].name,
      total_kilometers: trip.total_kilometers,
      include_stops: includeStops
    };
    
    // Send the updated trip data to the parent component
    onUpdateTrip(requestData);
  };
  
  return (
    <Box>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs>
          <Typography variant="h6">
            Customize Trip Stops
          </Typography>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            size="small"
          >
            Add Stop
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSaveChanges}
          >
            Update Trip
          </Button>
        </Grid>
      </Grid>
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        You can remove intermediate stops or add new ones. Start and end points cannot be changed.
      </Typography>
      
      <Paper variant="outlined" sx={{ mt: 2 }}>
        <List>
          {stops.map((stop, index) => (
            <React.Fragment key={index}>
              {index > 0 && <Divider />}
              <ListItem>
                <ListItemIcon>
                  <LocationOnIcon 
                    color={
                      stop.type === 'start' ? 'primary' :
                      stop.type === 'end' ? 'secondary' : 'action'
                    }
                  />
                </ListItemIcon>
                <ListItemText 
                  primary={stop.name}
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Chip 
                        label={stop.type.charAt(0).toUpperCase() + stop.type.slice(1)} 
                        size="small"
                        color={
                          stop.type === 'start' ? 'primary' :
                          stop.type === 'end' ? 'success' : 'warning'
                        }
                        sx={{ mr: 1 }}
                      />
                      {stop.distance_from_previous && (
                        <Typography variant="body2" color="text.secondary">
                          {stop.distance_from_previous} km from previous
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  {stop.type === 'intermediate' && (
                    <IconButton edge="end" onClick={() => handleRemoveStop(index)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Paper>
      
      {/* Add Stop Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Add a New Stop</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Stop Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newStopName}
            onChange={(e) => setNewStopName(e.target.value)}
            helperText="Enter the name of a city or location to add as a stop"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleAddStop} variant="contained">Add Stop</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StopCustomizer;